import { AddCircle } from "@material-ui/icons"
import { isEmpty } from "lodash"
import PropTypes from "prop-types"
import { useCallback, useMemo } from "react"
import { useDropzone } from "react-dropzone"

import clsx from "clsx"
import Spinner from "~/components/Common/Spinner"
import useReadOnly from "~/hooks/useReadOnly"
import PermissionsWrapper from "../PermissionsWrapper"

const NewFileUpload = ({ className, ...props }) => {
  const isReadOnly = useReadOnly()

  const onDrop = useCallback(
    (acceptedFiles, rejectedFiles) => {
      if (rejectedFiles.length) {
        props?.onError(rejectedFiles)
        return
      } else if (props?.type === "srt") {
        props?.onDrop(acceptedFiles, props?.videoUploaded)
      } else {
        props?.onDrop(acceptedFiles)
      }
    },
    [props],
  )

  const disableDrag = useMemo(() => {
    return !isEmpty(props.children)
  }, [props.children])

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: props?.accepted ? props?.accepted : "image/*",
    maxFiles: 1,
    multiple: false,
    ...(props?.DropZoneProps || {}),
    noDrag: disableDrag,
    noDragEventsBubbling: disableDrag,
    disabled:
      props?.DropZoneProps?.disabled ||
      props?.showLoading ||
      isReadOnly ||
      false,
  })

  if (props.children) {
    return (
      <div className={clsx("flex relative", className && className)}>
        {props?.showLoading && (
          <div className="absolute top-0 left-0 right-0 bottom-0 z-10 bg-[rgba(0,0,0,0.25)] flex items-center justify-center">
            <Spinner />
          </div>
        )}
        <div {...getRootProps()} className="w-full">
          <input {...getInputProps()} id={props?.id || "file-upload"} />
          {props.children}
        </div>
      </div>
    )
  } else {
    return (
      <div className={clsx("w-full relative", className && className)}>
        <div
          {...getRootProps()}
          className={clsx(
            props?.type === "avatar" &&
              "w-full h-full absolute top-0 text-center bg-[#eaecee] cursor-pointer overflow-hidden flex flex-col rounded-[500px]",
            props?.type !== "avatar" &&
              "w-full h-full absolute top-0 text-center bg-[#D0D5DD] cursor-pointer overflow-hidden flex justify-center items-center rounded-[4px]",
          )}
        >
          {props?.showLoading && (
            <div className="absolute top-0 left-0 right-0 bottom-0 z-10 bg-[rgba(0,0,0,0.25)] flex items-center justify-center">
              <Spinner />
            </div>
          )}
          <input {...getInputProps()} id={props?.id || "file-upload"} />
          {props?.children && (
            <>
              <div></div>
              {props.children}
            </>
          )}
          {props.type === "avatar" ? (
            <div className="m-1">
              <PermissionsWrapper permissionGroup="admin">
                <p>{props?.title || "Drag image"}</p>
                <p>
                  or <strong>click to browse files</strong> from your computer
                </p>
              </PermissionsWrapper>
            </div>
          ) : (
            <div>
              <PermissionsWrapper permissionGroup="admin">
                {props.showLoading ? (
                  <p>{props?.progressMessage || "Uploading..."}</p>
                ) : (
                  <>
                    <AddCircle
                      color="primary"
                      style={{ marginBottom: "4px" }}
                    />
                    <p>{props?.title || "Drop file to upload here"}</p>
                  </>
                )}
              </PermissionsWrapper>
            </div>
          )}
        </div>
      </div>
    )
  }
}

NewFileUpload.propTypes = {
  id: PropTypes.any,
  DropZoneProps: PropTypes.object,
  title: PropTypes.string,
  onDrop: PropTypes.func.isRequired,
  onError: PropTypes.func,
  children: PropTypes.node,
  showLoading: PropTypes.bool,
  height: PropTypes.string,
  type: PropTypes.string,
  videoUploaded: PropTypes.bool,
  accepted: PropTypes.string,
  childHeight: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  childWidth: PropTypes.number,
  progressMessage: PropTypes.string,
  className: PropTypes.string,
}

export default NewFileUpload
